import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { 
  Shield, 
  CheckCircle, 
  Users, 
  TrendingUp, 
  Award,
  Eye,
  Heart,
  Zap,
  Download,
  ArrowRight,
  ChevronRight,
  Sparkles,
  AlertTriangle,
  Camera,
  FileImage,
  Film
} from 'lucide-react';
import HumanVerifiedBadge from '../components/HumanVerifiedBadge';

const HumanVerifiedLanding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = '';  // Use relative URLs

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get('/api/admin/verifications/stats');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load stats:', error);
      setLoading(false);
    }
  };

  const handleGetVerified = () => {
    if (!user) {
      navigate('/auth');
    } else if (user.role === 'artist' || user.role === 'author') {
      navigate('/verification');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <div className="text-center">
            {/* Badge Showcase */}
            <div className="flex justify-center mb-8 animate-bounce">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full p-4">
                <Shield className="w-16 h-16 text-white" fill="currentColor" />
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
              <span className="text-white">The Home of</span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
                Verified Human Artists
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-zinc-300 mb-4 max-w-3xl mx-auto">
              Trap Comix stands against AI spam. Every verified creator is a real artist, 
              creating authentic, human-made art.
            </p>

            <div className="flex items-center justify-center gap-2 text-sm text-zinc-400 mb-12">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>No AI. No Fakes. Just Real Art.</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetVerified}
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-lg text-lg flex items-center justify-center gap-2 transition-all transform hover:scale-105"
              >
                <Shield className="w-5 h-5" />
                Get Verified Now
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="#how-it-works"
                className="px-8 py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-lg text-lg flex items-center justify-center gap-2 transition-all"
              >
                Learn How It Works
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>

            {/* Live Stats Preview */}
            {stats && (
              <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                <StatCard value={stats.total_verified} label="Verified Creators" icon={<Shield className="w-6 h-6" />} />
                <StatCard value={stats.total_creators} label="Total Creators" icon={<Users className="w-6 h-6" />} />
                <StatCard value={`${stats.verification_rate}%`} label="Verification Rate" icon={<TrendingUp className="w-6 h-6" />} />
                <StatCard value={stats.pending} label="Under Review" icon={<Eye className="w-6 h-6" />} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-2 mb-6">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-semibold">The Problem</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                AI is Flooding the Internet with Fake Art
              </h2>
              <div className="space-y-4 text-zinc-300 text-lg">
                <p>
                  Every day, thousands of AI-generated images flood art platforms. 
                  It's becoming impossible to tell what's real and what's synthetic.
                </p>
                <p>
                  Real artists lose visibility. Fans get deceived. The creative community suffers.
                </p>
                <p className="text-white font-semibold">
                  Trap Comix refuses to be part of the problem.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-2xl p-8 border-2 border-red-500/30">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-red-400">
                    <X className="w-6 h-6" />
                    <span className="font-semibold">AI-generated spam</span>
                  </div>
                  <div className="flex items-center gap-3 text-red-400">
                    <X className="w-6 h-6" />
                    <span className="font-semibold">Fake artist profiles</span>
                  </div>
                  <div className="flex items-center gap-3 text-red-400">
                    <X className="w-6 h-6" />
                    <span className="font-semibold">Stolen and copied work</span>
                  </div>
                  <div className="flex items-center gap-3 text-red-400">
                    <X className="w-6 h-6" />
                    <span className="font-semibold">Zero authenticity</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-20 bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-5 h-5 text-blue-400" />
              <span className="text-blue-400 font-semibold">The Solution</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Human Verified Creator System
            </h2>
            <p className="text-xl text-zinc-300 max-w-3xl mx-auto">
              A badge that means something. Proof of authentic, human-made artistry.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<CheckCircle className="w-12 h-12" />}
              title="Real Proof Required"
              description="Sketches, WIP shots, process videos, or reference photos. Show your creative journey."
              color="green"
            />
            <FeatureCard
              icon={<Users className="w-12 h-12" />}
              title="Human Review"
              description="Our admin team personally reviews every submission. No automated approval."
              color="blue"
            />
            <FeatureCard
              icon={<Award className="w-12 h-12" />}
              title="Trusted Badge"
              description="Earn the verified badge displayed on all your content across the platform."
              color="purple"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              How to Get Verified
            </h2>
            <p className="text-xl text-zinc-300 max-w-2xl mx-auto">
              Simple 3-step process to prove you're a real artist
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <ProcessStep
              number="1"
              icon={<Upload className="w-8 h-8" />}
              title="Submit Your Proof"
              description="Upload sketches, WIP shots, process videos, or workspace photos that show your creative process."
              proofTypes={[
                { icon: <FileImage />, label: "Sketches" },
                { icon: <Camera />, label: "WIP Shots" },
                { icon: <Film />, label: "Process Videos" }
              ]}
            />
            <ProcessStep
              number="2"
              icon={<Eye className="w-8 h-8" />}
              title="Admin Reviews"
              description="Our team examines your submission to verify it shows authentic human creation. Usually takes 1-3 days."
            />
            <ProcessStep
              number="3"
              icon={<Award className="w-8 h-8" />}
              title="Get Your Badge"
              description="Once approved, the Human Verified badge appears on your profile and all your content instantly."
              badge={<HumanVerifiedBadge size="default" />}
            />
          </div>

          <div className="text-center mt-12">
            <button
              onClick={handleGetVerified}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-lg text-lg inline-flex items-center gap-2"
            >
              Start Your Verification
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Creator Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Why Get Verified?
            </h2>
            <p className="text-xl text-zinc-300">
              Benefits for verified creators
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <BenefitCard
              icon={<Shield className="w-8 h-8 text-blue-400" />}
              title="Trust Badge"
              description="Display the Human Verified badge on your profile and all your content"
            />
            <BenefitCard
              icon={<TrendingUp className="w-8 h-8 text-green-400" />}
              title="Higher Visibility"
              description="Verified creators get priority in search and browse features"
            />
            <BenefitCard
              icon={<Heart className="w-8 h-8 text-red-400" />}
              title="Fan Trust"
              description="Fans know they're supporting real artists, not AI content"
            />
            <BenefitCard
              icon={<Zap className="w-8 h-8 text-yellow-400" />}
              title="Priority Support"
              description="Get faster responses and dedicated creator support"
            />
            <BenefitCard
              icon={<Award className="w-8 h-8 text-purple-400" />}
              title="Featured Content"
              description="Higher chance of being featured on homepage and newsletters"
            />
            <BenefitCard
              icon={<Users className="w-8 h-8 text-cyan-400" />}
              title="Community Recognition"
              description="Join the elite group of verified authentic creators"
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              What Verified Creators Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <TestimonialCard
              quote="Getting verified was easy and now my fans trust that every piece is 100% human-made. Game changer!"
              author="Marcus Chen"
              role="Digital Artist"
              verified={true}
            />
            <TestimonialCard
              quote="The verification badge helped me stand out in a sea of AI content. My engagement went up 40%!"
              author="Jade Williams"
              role="Comic Creator"
              verified={true}
            />
            <TestimonialCard
              quote="Finally, a platform that values real artistry. Proud to be verified on Trap Comix."
              author="Alex Rivera"
              role="Illustrator"
              verified={true}
            />
          </div>
        </div>
      </section>

      {/* Press Kit Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500/5 to-red-500/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-zinc-900 rounded-2xl p-12 border border-zinc-800">
            <Download className="w-16 h-16 mx-auto mb-6 text-orange-400" />
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              Press Kit & Brand Assets
            </h2>
            <p className="text-zinc-300 mb-8 text-lg">
              Download our Human Verified badge, logos, and announcement materials for your website or social media.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2">
                <Download className="w-5 h-5" />
                Download Badge Assets
              </button>
              <button className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2">
                <Download className="w-5 h-5" />
                Download Press Release
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HumanVerifiedBadge size="large" className="mx-auto mb-8" />
          <h2 className="text-4xl md:text-6xl font-black mb-6">
            Join the Verified Community
          </h2>
          <p className="text-xl text-zinc-300 mb-12">
            Be part of the movement. Show the world you're a real artist.
          </p>
          <button
            onClick={handleGetVerified}
            className="px-12 py-5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-lg text-xl inline-flex items-center gap-3 transform hover:scale-105 transition-all"
          >
            <Shield className="w-6 h-6" />
            Get Verified Today
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </section>
    </div>
  );
};

// Component: Stat Card
const StatCard = ({ value, label, icon }) => (
  <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
    <div className="flex items-center justify-between mb-2">
      <div className="text-blue-400">{icon}</div>
    </div>
    <div className="text-3xl font-bold text-white mb-1">{value}</div>
    <div className="text-sm text-zinc-400">{label}</div>
  </div>
);

// Component: Feature Card
const FeatureCard = ({ icon, title, description, color }) => {
  const colors = {
    green: 'from-green-500/20 to-emerald-500/20 border-green-500/30',
    blue: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30',
    purple: 'from-purple-500/20 to-pink-500/20 border-purple-500/30'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border-2 rounded-xl p-8`}>
      <div className="text-white mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-zinc-300">{description}</p>
    </div>
  );
};

// Component: Process Step
const ProcessStep = ({ number, icon, title, description, proofTypes, badge }) => (
  <div className="relative">
    <div className="bg-zinc-900 rounded-xl p-8 border-2 border-blue-500/30 h-full">
      <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
        {number}
      </div>
      <div className="text-blue-400 mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-zinc-300 mb-4">{description}</p>
      {proofTypes && (
        <div className="flex gap-2 flex-wrap">
          {proofTypes.map((type, idx) => (
            <div key={idx} className="flex items-center gap-1 bg-zinc-800 px-3 py-1 rounded-full text-sm text-zinc-400">
              {React.cloneElement(type.icon, { className: 'w-3 h-3' })}
              <span>{type.label}</span>
            </div>
          ))}
        </div>
      )}
      {badge && (
        <div className="mt-4 flex justify-center">
          {badge}
        </div>
      )}
    </div>
  </div>
);

// Component: Benefit Card
const BenefitCard = ({ icon, title, description }) => (
  <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 hover:border-blue-500/30 transition-colors">
    <div className="mb-4">{icon}</div>
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-zinc-400 text-sm">{description}</p>
  </div>
);

// Component: Testimonial Card
const TestimonialCard = ({ quote, author, role, verified }) => (
  <div className="bg-zinc-900 rounded-xl p-8 border border-zinc-800">
    <div className="text-zinc-300 mb-6 text-lg italic">"{quote}"</div>
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-2">
          <div className="font-bold text-white">{author}</div>
          {verified && <HumanVerifiedBadge size="small" showText={false} />}
        </div>
        <div className="text-sm text-zinc-400">{role}</div>
      </div>
    </div>
  </div>
);

// X icon component
const X = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Upload icon component
const Upload = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

export default HumanVerifiedLanding;
